import { useRef, useState } from "react";
import { Upload, X, File as FileIcon } from "lucide-react";
import { openPendingFile } from "../../utils/download";

interface FileDropZoneProps {
  files?: File[];
  onFilesChange?: (files: File[]) => void;
  /** Fire-and-forget mode: picked files are passed here (e.g. straight to an
   *  upload mutation) and no selected-files chip list is rendered. */
  onSelect?: (files: File[]) => void;
  multiple?: boolean;
  accept?: string;
  className?: string;
  disabled?: boolean;
  /** Overrides the default "Przeciągnij plik(i)..." prompt. */
  label?: string;
}

export function FileDropZone({
  files = [],
  onFilesChange,
  onSelect,
  multiple = false,
  accept,
  className,
  disabled = false,
  label,
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  function addFiles(incoming: FileList | File[]) {
    const arr = Array.from(incoming);
    if (arr.length === 0) return;
    if (onSelect) {
      onSelect(multiple ? arr : arr.slice(0, 1));
      return;
    }
    if (multiple) {
      onFilesChange?.([...files, ...arr]);
    } else {
      onFilesChange?.(arr.slice(0, 1));
    }
  }

  return (
    <div className={className}>
      <div
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!disabled) addFiles(e.dataTransfer.files); }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false); }}
        onClick={() => { if (!disabled) inputRef.current?.click(); }}
        className={`border-2 border-dashed px-4 py-4 flex flex-col items-center gap-2 transition-colors ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        } ${
          dragOver
            ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
            : "border-gray-300 dark:border-gray-600 hover:border-[var(--color-primary)] hover:bg-gray-50 dark:hover:bg-gray-700/30"
        }`}
      >
        <Upload size={18} className="text-gray-400 dark:text-gray-500" />
        <p className="text-sm text-gray-400 dark:text-gray-500 text-center">
          {label ??
            (multiple
              ? "Przeciągnij pliki lub kliknij, aby wybrać"
              : "Przeciągnij plik lub kliknij, aby wybrać")}
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          disabled={disabled}
          onChange={(e) => {
            if (e.target.files) {
              addFiles(e.target.files);
              e.target.value = "";
            }
          }}
        />
      </div>

      {files.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <li
              key={i}
              className="flex items-center gap-1.5 bg-[var(--bg-elevated)] px-2 py-1 text-xs font-mono text-[var(--text-secondary)]"
            >
              <FileIcon size={12} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openPendingFile(f); }}
                className="max-w-[180px] truncate hover:underline hover:text-[var(--color-primary)]"
                title={`${f.name} — kliknij, aby podejrzeć`}
              >
                {f.name}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onFilesChange?.(files.filter((_, j) => j !== i)); }}
                className="text-gray-400 hover:text-red-500 ml-0.5"
              >
                <X size={10} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
